import {
  Clock, Layers, Users, Building2, ShieldCheck, Award, Globe,
  HeartPulse, Heart, Truck, ShoppingBag, Briefcase, Leaf, Factory, Utensils,
  Cpu, BarChart3, Brain, Database, FileCheck, Zap, Settings,
  Target, Sparkles, ArrowRight, Check
} from "lucide-react";

export const ABOUT_HERO = {
  eyebrow: "About Asterisk Computer (FE) Pte Ltd",
  headline: "Singapore's Trusted SME Digital Transformation Partner",
  since: "Since 1988",
  subheadline:
    "For over 38 years, Asterisk has helped small and medium enterprises modernise, scale, and adapt through every wave of technological evolution — from on-premise systems to AI and Data Science.",
};

export const ABOUT_STATS = [
  { value: "180+", label: "Projects Delivered", icon: Layers },
  { value: "100+", label: "Customers Served", icon: Users },
  { value: "10+", label: "Industries Supported", icon: Building2 },
  { value: "2M+", label: "Support Hours", icon: Clock },
];

export const ABOUT_STORY = {
  headline: "Tested by Time. Strengthened by Innovation.",
  paragraphs: [
    "Founded in 1988 — before 'Digital Transformation' was a buzzword, before cloud computing, AI, or ERP were mainstream — Asterisk Computer is one of Singapore's longest-running SME digitalisation specialists.",
    "While many firms pivot, merge, or disappear, Asterisk has remained independently driven, financially disciplined, customer-focused, and technically evolving.",
    "We evolved from accounting systems to ERP, from ERP to analytics, from analytics to AI. Asterisk represents stability with innovation.",
  ],
  waves: [
    "On-premise systems",
    "Client-server architecture",
    "Web applications",
    "Cloud ERP",
    "Business Intelligence",
    "Automation",
    "AI & Data Science",
  ],
};

export const ABOUT_SUPPORTED = [
  "Regulatory changes",
  "Tax reforms",
  "Industry digitisation mandates",
  "Regional expansion",
  "Economic downturns",
  "Technological disruption",
];

export const ABOUT_INDUSTRIES = [
  { name: "Healthcare & Long-Term Care", icon: HeartPulse },
  { name: "Non-Profit Organisations", icon: Heart },
  { name: "Distribution & Logistics", icon: Truck },
  { name: "Food & Cold Storage", icon: Utensils },
  { name: "Manufacturing", icon: Factory },
  { name: "Retail", icon: ShoppingBag },
  { name: "Hospitality", icon: Building2 },
  { name: "Professional Services", icon: Briefcase },
  { name: "Sustainability & ESG", icon: Leaf },
];

export const ABOUT_ECOSYSTEM = [
  { title: "ERP & CRM", icon: Database },
  { title: "Patient Accounting (CareOps 365)", icon: HeartPulse },
  { title: "Data Science & Analytics", icon: BarChart3 },
  { title: "AI Design Factory", icon: Brain },
  { title: "Automation & Document Intelligence", icon: Zap },
  { title: "ESG & Sustainability Reporting", icon: Leaf },
];

export const ABOUT_CONTINUITY = {
  headline: "More Than Projects — Continuity",
  description: "We focus on lifecycle, not just go-live. Over 2 million support hours represent thousands of resolved tickets, upgrades, compliance adjustments, and continuous improvements.",
  elements: [
    "Structured solution design",
    "Careful implementation",
    "User enablement",
    "Post-go-live optimisation",
    "Long-term support contracts",
  ],
};

export const ABOUT_VISION = {
  vision: "To be Singapore's most trusted and enduring SME Digital Transformation Partner — shaping intelligent, resilient, and future-ready enterprises across Asia Pacific.",
  mission: "To simplify complex technologies and deliver practical, scalable digital solutions that empower SMEs to grow confidently, operate intelligently, and innovate sustainably — with integrity at every step.",
};

export const ABOUT_VALUES = [
  { title: "Integrity Before Innovation", description: "Technology changes. Trust must remain constant.", icon: ShieldCheck },
  { title: "Practical Over Theoretical", description: "Deliver solutions that work in real business environments.", icon: Settings },
  { title: "Partnership Over Transactions", description: "Build long-term relationships, not short-term engagements.", icon: Users },
  { title: "Innovation With Discipline", description: "Adopt AI, automation, and data science responsibly — aligned to measurable value.", icon: Sparkles },
];

export const ABOUT_WHY_STAY = [
  "Structured governance",
  "Industry familiarity",
  "Responsible implementation",
  "Clear documentation",
  "Strong support culture",
  "Leadership stability",
];

export const ABOUT_FUTURE = [
  "AI embedded into daily workflows",
  "Real-time financial intelligence",
  "Sustainable reporting as standard practice",
  "Cross-border digital integration",
  "Automation-first operations",
];

export const ABOUT_CREDENTIALS = [
  "24+ years Microsoft ERP expertise (since 2001)",
  "Microsoft Solutions Partner (Business Applications)",
  "Industry solutions published on Microsoft AppSource",
  "Long-term support relationships spanning 10–20 years",
];


export const ABOUT_LOCATIONS = {
  headline: "Where Innovation Lives",
  description:
    "Digital transformation should not feel distant. It should feel accessible, accountable, and grounded in real partnership. Our offices in Singapore and Kuala Lumpur are more than operational bases — they are innovation hubs where strategy becomes architecture, architecture becomes systems, and systems deliver measurable business outcomes.",
  offices: [
    {
      city: "Singapore",
      role: "HQ and Innovation & Architecture Hub",
      company: "Asterisk Computer (FE) Pte. Ltd.",
      address: "35 Tannery Road #02-07 Tannery Block, Ruby Industrial Complex, Singapore 347740",
      phone: "+65 6748 8727",
      services: [
        "Patient Accounting & Billing for Long-Term Care",
        "Microsoft ERP & CRM implementations",
        "AI Design Factory for SMEs",
        "Microsoft Fabric–powered Data Science",
        "Sustainability & ESG compliance systems",
      ],
      detail: "Operating within one of Asia's most advanced digital ecosystems allows us to maintain global standards in governance, cybersecurity, compliance, and performance.",
      lat: 1.3345,
      lng: 103.8675,
    },
    {
      city: "Kuala Lumpur",
      role: "Regional Delivery & Growth Hub",
      company: "Asterisk Business Systems Sdn Bhd",
      address: "Menara Sentral Vista, Jalan Sultan Abdul Samad, Brickfields, 50470 Kuala Lumpur",
      phone: "+603 2856 9193",
      services: [
        "Structured digital transformation programs",
        "ERP modernisation and operational efficiency",
        "AI Design Factory for SMEs",
        "Analytics and automation acceleration",
        "Sustainable supply chain enablement",
      ],
      detail: "Strengthens cross-border execution capability and regional scalability with strong local insight and regional coordination.",
      lat: 3.1319,
      lng: 101.6841,
    },
    {
      city: "Ho Chi Minh City",
      role: "Regional Delivery & Growth Hub",
      company: "Asterisk Business Solutions Vietnam Co., Ltd",
      address: "6th Floor - Rosana Tower - 60 Nguyen Dinh Chieu Street, Tan Dinh Ward, Ho Chi Minh City, Vietnam",
      phone: "+84 768258789",
      services: [
        "Structured digital transformation programs",
        "ERP modernisation and operational efficiency",
        "AI Design Factory for SMEs",
        "Analytics and automation acceleration",
        "Sustainable supply chain enablement",
      ],
      detail: "Strengthens cross-border execution capability and regional scalability with strong local insight and regional coordination.",
      lat: 10.7891,
      lng: 106.6906,
    },
  ],
  closingHeadline: "Built for Proximity. Designed for Scale.",
  closingPoints: [
    "Real offices",
    "Real consultants",
    "Real accountability",
    "Long-term partnership",
  ],
  closingText: "We combine local presence with regional reach, and SME agility with enterprise discipline. Transformation is not abstract. It happens here.",
};
